import { nextTick } from "vue";
import { copy } from "@/shared/utils/string";
import { EventBus } from "@/shared/projects/event-bus.ts";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { playerCartService } from "../services/player-cart.service";
import { playerUploadService } from "../services/player-upload.service";
import { cloneDeep, randomId } from "@/shared/utils/helpers.js";
import { playerSnapshotService } from "@/apps/player/store/player-snapshot.service";
import { t } from "@/i18n";
import {
    PLAYER_EXAMPLE_DESC_EN,
    PLAYER_EXAMPLE_DESC_PL,
    PLAYER_EXAMPLE_TEXT_EN,
    PLAYER_EXAMPLE_TEXT_PL,
    PLAYER_FILE_TYPES,
    PLAYER_HEADER_SIDES,
    PLAYER_HEADER_TYPES,
    PLAYER_VIDEO_SOURCES,
} from "@/apps/player/services/players.service";
import { projectsService } from "@/shared/projects/projects.service";
import { certificateTemplatesService } from "@/apps/player/services/certificate-templates.service";
import { DownloadFileIcon, PdfIcon, TextFileIcon, VideoIcon } from "@easy/ui/icons";
import { isEmpty } from "lodash";
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("player.store");

export default {
    namespaced: true,
    state: () => ({
        player: {},
        files: [],
        currentFile: {},
        headers: [],
        watched: [],
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
        certificate: {},
        certificateContentGhost: {},
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.player,
        templates: () => [
            {
                value: t("faq.templates.empty"),
                id: "empty",
            },
            {
                value: t("player.files.video"),
                icon: VideoIcon,
                id: PLAYER_FILE_TYPES.video,
            },
            {
                value: t("player.files.text"),
                icon: TextFileIcon,
                id: PLAYER_FILE_TYPES.text,
            },
            {
                value: t("player.files.file"),
                icon: DownloadFileIcon,
                id: PLAYER_FILE_TYPES.file,
            },
            {
                value: "PDF",
                icon: PdfIcon,
                id: PLAYER_FILE_TYPES.pdf,
            },
        ],
        hasCartProductConnected: ({ player }) => Boolean((player?.ecProductIds || []).length),
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { player, files, headers, certificate, snapshot = {} }) {
            state.player = playerSnapshotService.makeSnapshotState(
                state,
                "player",
                player,
                snapshot
            );
            state.files = playerSnapshotService.makeSnapshotState(state, "files", files, snapshot);
            const noHeadersFiles = state.files.filter(
                (f) => f.type !== PLAYER_FILE_TYPES.header && !f._deleted && !f.hide
            );
            state.currentFile = noHeadersFiles.length ? noHeadersFiles[0] : {};
            state.headers = playerSnapshotService.makeSnapshotState(
                state,
                "headers",
                headers,
                snapshot
            );
            state.certificate = playerSnapshotService.makeSnapshotState(
                state,
                "certificate",
                certificate,
                snapshot
            );
            // We need to Ghost the content, to reflect changes (as .content is an Object)
            state.certificate.content = playerSnapshotService.makeSnapshotState(
                state,
                "certificateContentGhost",
                state.certificate.content || {},
                snapshot
            );
            settleDefaultsWhenCertificateEmpty(state.certificate, () => {
                const progressThreshold = certificateTemplatesService.getDefaultProgressThreshold();
                const templateName = certificateTemplatesService.getDefaultName();
                const content = { courseName: player.name };
                logger.debug(
                    "I will SETTLE default progressThreshold & templateName & courseName !!!",
                    progressThreshold,
                    templateName,
                    content
                );
                state.certificate.progressThreshold = progressThreshold;
                state.certificate.templateName = templateName;
                state.certificate.content = content;
                // action is from callback, so me must repeat here (assuring no changes if action fired)
                state.hasChanges = false;
            });
            state.hasChanges = false;
            setTimeout(() => {
                delete state.player._changed;
            }, 100);
        },
        clearProject(state) {
            state.player = {};
            state.files = [];
            state.currentFile = {};
            state.headers = [];
            state.certificate = {};
            playerSnapshotService.clearSnapshot(state, "player", {});
            playerSnapshotService.clearSnapshot(state, "files", []);
            playerSnapshotService.clearSnapshot(state, "headers", []);
            playerSnapshotService.clearSnapshot(state, "certificate", {});
            playerSnapshotService.clearSnapshot(state, "certificateGhost", {});
        },
        setFiles(state, payload) {
            if (payload) {
                state.files = playerSnapshotService.makeSnapshotState(state, "files", payload, {});
                state.hasChanges = true;
            }

            state.files.forEach((file, index) => {
                file.order = index;
                if (!file._created && !file._deleted) {
                    file["_changed"] = true;
                }
            });
        },
        changeFile(state, payload) {
            const updatedFile = Object.assign({}, payload);

            const index = state.files.map((f) => f.id).indexOf(payload.id);
            if (index > -1) {
                state.files.splice(index, 1, updatedFile);
            }
        },

        removeFile(state, file) {
            if (file._created) {
                const index = state.files.map((f) => f.id).indexOf(file.id);
                if (index > -1) {
                    state.files.splice(index, 1);
                }
            } else {
                if (file._changed) {
                    delete file._changed;
                }
                file["_deleted"] = true;
            }

            const indexInWatched = state.watched.indexOf(file.id);
            if (indexInWatched > -1) {
                state.watched.splice(indexInWatched, 1);
            }
        },
        addFile(state, file) {
            const newFile = {
                type: "video",
                text: "",
                videoSrc: "vimeo",
                src: "",
                info: "",
                _created: true,
                id: randomId(),
                playerId: state.player.id,
                order: state.files.length,
                ...file,
            };
            state.files.push(newFile);
        },
        addHeader(state, header) {
            header["_created"] = true;
            header.id = randomId();
            header.playerId = state.player.id;
            state.headers.push(header);
        },
        changeHeader(state, payload) {
            const updatedHeader = Object.assign({}, payload);

            const index = state.headers.map((f) => f.id).indexOf(payload.id);
            if (index > -1) {
                state.headers.splice(index, 1, updatedHeader);
            }
        },
        removeHeader(state, header) {
            if (header._created) {
                const index = state.headers.map((f) => f.id).indexOf(header.id);
                if (index > -1) {
                    state.headers.splice(index, 1);
                }
            } else {
                delete header._changed;
                header["_deleted"] = true;
            }
        },
        setHeaders(state, payload) {
            if (payload) {
                state.headers = playerSnapshotService.makeSnapshotState(
                    state,
                    "headers",
                    payload,
                    {}
                );
                state.hasChanges = true;
            }

            const numberOfLeftHeaders = state.headers.filter(
                (header) => header.side === "left"
            ).length;

            //first oder left headers
            state.headers
                .filter((header) => header.side === "left")
                .forEach((h, index) => {
                    h.order = index;
                    if (!h._created && !h._deleted) {
                        h._changed = true;
                    }
                });

            //then oder right headers
            state.headers
                .filter((header) => header.side === "right")
                .forEach((h, index) => {
                    h.order = index + numberOfLeftHeaders;
                    if (!h._created && !h._deleted) {
                        h._changed = true;
                    }
                });
        },
        setCurrentFile(state, payload) {
            state.currentFile = payload;
        },
        toggleFileStatus(state, payload) {
            const index = state.watched.indexOf(payload);
            if (index !== -1) {
                state.watched.splice(index, 1);
            } else {
                state.watched.push(payload);
            }
        },
        addEcProduct(state, payload) {
            state.player.ecProductIds.push(payload);
        },
        removeEcProduct(state, payload) {
            const index = state.player.ecProductIds.indexOf(payload);
            if (index !== -1) {
                state.player.ecProductIds.splice(index, 1);
            }
        },
    },
    actions: {
        refreshIframe({ state, dispatch }) {
            logger.trace("refreshIframe() action taken");
            const player = cloneDeep(state.player);
            player.files = cloneDeep(state.files);
            player.headers = cloneDeep(state.headers);
            player.ecProductIds = JSON.stringify(player.ecProductIds);
            player.certificate = cloneDeep(state.certificate);
            dispatch("sendIframeChildMessage", { action: "playerDataChanged", payload: player });
        },
        sendIframeChildMessage({ state, getters }, { action, payload }) {
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && state.player.id) {
                state.iframe.contentWindow.postMessage({ action, payload }, getters.appUrl);
            }
        },
        getFileById({ state }, id) {
            const { files = [] } = state || {};
            return files.find((f) => f.id === Number(id));
        },
        async getECProducts({ rootState, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            rootState.waiting = true;
            const data = await playerCartService.getECProducts();
            rootState.waiting = false;
            return data;
        },
        async connectWithECProduct({ state, commit, dispatch }, { id, add }) {
            if (add) {
                await playerCartService.connectToCart({
                    playerUuid: state.player.uuid,
                    ecProductId: id,
                });
                commit("addEcProduct", id);
            } else {
                await playerCartService.disconnectFromCart({
                    playerUuid: state.player.uuid,
                    ecProductId: id,
                });
                commit("removeEcProduct", id);
            }

            dispatch("player/projects/mergeSnapshotChanges", null, { root: true });
        },
        async removeFile({ commit }, payload) {
            commit("removeFile", payload);
            commit("setFiles");
        },
        async cloneFile({ commit }, file) {
            const clonedFile = cloneDeep(file);
            delete clonedFile.id;
            delete clonedFile._changed;
            commit("addFile", clonedFile);
            commit("setFiles");
        },
        async uploadSrc({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { src } = await playerUploadService.fileSrc({
                    file: payload.src,
                    playerId: state.player.id,
                    fileId: payload.file.id,
                });
                payload.file.src = rootState.globalUrl + src;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async removeHeader({ commit }, payload) {
            commit("removeHeader", payload);
            commit("setHeaders");
        },
        async cloneHeader({ commit }, header) {
            const clonedHeader = cloneDeep(header);
            commit("addHeader", clonedHeader);
        },
        async uploadLogo({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { logo } = await playerUploadService.playerLogo({
                    file: payload.file,
                    playerId: state.player.id,
                });

                state.player.logo = logo;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadImage({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { logo } = await playerUploadService.playerLogo({
                    file: payload.file,
                    playerId: state.player.id,
                });

                rootState.waiting = false;
                return logo;
            } catch {
                return null;
            }
        },
        async uploadIcon({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { icon } = await playerUploadService.pageIcon({
                    file: payload.file,
                    playerId: state.player.id,
                });

                state.player.pageIcon = icon;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        showNoFilesModal({ commit }) {
            commit(
                "showModal",
                {
                    type: "info",
                    title: t("player.empty.title"),
                    desc: t("player.empty.desc"),
                    cancelBtn: t("shared.cancel"),
                    actionBtn: t("player.empty.cta"),
                    callback: () => {
                        EventBus.$emit("openSidebar", "data");
                        setTimeout(() => EventBus.$emit("createFile"), 100);
                    },
                },
                { root: true }
            );
        },
        async publishProject({ state, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            if (!state.files.length) {
                dispatch("showNoFilesModal");
                return;
            }

            // @fix[EAS-2209]: publish autosave
            if (state.hasChanges) {
                await dispatch("player/projects/saveSnapshot", null, { root: true });
            }

            dispatch("hostCode");
        },
        async unpublishPlayer({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.player.uuid });
            commit("setUnpublished", state.player, { root: true });
            dispatch("player/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        unpublish({ _ }, { uuid }) {
            return projectsService.publish({
                tool: "player",
                uuid,
                publish: false,
            });
        },
        showLinkModal({ state, rootState, commit }) {
            const modalData = {
                type: "code",
                title: t("player.share_modal.title"),
                code: `${rootState.globalUrl}/players/${state.player.uuid}`,
                actionBtn: t("player.share_modal.copy_link"),
                callback: () => {
                    copy(`${rootState.globalUrl}/players/${state.player.uuid}`);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.link_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode({ state, rootState, rootGetters, commit, dispatch }) {
            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.player);

            await nextTick();

            try {
                await projectsService.publish({
                    tool: "player",
                    uuid: state.player.uuid,
                    publish: true,
                });

                commit("setPublished", state.player, { root: true });
                dispatch("player/projects/mergeSnapshotChanges", null, { root: true });
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
            } catch {
                rootState.waiting = false;
            }
        },
        changeFontFamily({ state }, payload) {
            state.player.fontFamily = payload;
        },
        fillWithExample({ rootState, state, commit }, { template, name, website }) {
            const file = {
                description:
                    rootState.lang === "en" ? PLAYER_EXAMPLE_DESC_EN : PLAYER_EXAMPLE_DESC_PL,
                info: "3 min",
            };
            if (template === "empty") {
                commit("addFile", {
                    ...file,
                    name: t("player.example.empty"),
                    type: PLAYER_FILE_TYPES.video,
                    videoSrc: PLAYER_VIDEO_SOURCES.youtube,
                    src: "https://www.youtube.com/watch?v=LxIR7eFw8HI",
                });
            }
            if (template === PLAYER_FILE_TYPES.video) {
                commit("addFile", {
                    ...file,
                    name: t("player.example.video"),
                    type: PLAYER_FILE_TYPES.video,
                    videoSrc: PLAYER_VIDEO_SOURCES.youtube,
                    src: "https://www.youtube.com/watch?v=LxIR7eFw8HI",
                });
            }
            if (template === PLAYER_FILE_TYPES.text) {
                commit("addFile", {
                    ...file,
                    name: t("player.example.text"),
                    type: PLAYER_FILE_TYPES.text,
                    text: rootState.lang === "en" ? PLAYER_EXAMPLE_TEXT_EN : PLAYER_EXAMPLE_TEXT_PL,
                });
            }
            if (template === PLAYER_FILE_TYPES.file) {
                commit("addFile", {
                    ...file,
                    name: t("player.example.file"),
                    type: PLAYER_FILE_TYPES.file,
                    src: "https://app.easy.tools/players/files/2024-12-03/96b88e825a77485b99ddf0b07e729025.pdf",
                });
            }
            if (template === PLAYER_FILE_TYPES.pdf) {
                commit("addFile", {
                    ...file,
                    name: t("player.example.pdf"),
                    type: PLAYER_FILE_TYPES.pdf,
                    src: "https://app.easy.tools/players/files/2024-12-03/96b88e825a77485b99ddf0b07e729025.pdf",
                });
            }

            if (name) {
                state.player.pageTitle = name;
                state.player.footerText = name;

                const header = {
                    name,
                    type: website ? PLAYER_HEADER_TYPES.button : PLAYER_HEADER_TYPES.text,
                    side: PLAYER_HEADER_SIDES.right,
                    href: website,
                };

                commit("addHeader", header);
            }
        },
    },
};

// Private functions / resolvers:
function settleDefaultsWhenCertificateEmpty(certificate = {}, actionToTake) {
    const certCopy = cloneDeep(certificate);
    const contentEmpty = isEmpty(certCopy?.content || {});
    delete certCopy.content;
    const certEmpty = isEmpty(certCopy);
    logger.trace("settleDefaultsWhenCertificateEmpty() action taken");
    if (contentEmpty && certEmpty) {
        // Settle defaults:
        actionToTake();
    }
}
